// Mixer console: a channel strip per track + master. Faders, pan, mute/solo,
// inline meters, and an insert-FX list that opens the effect editor.
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el, gainToDb } from "../util";
import { EFFECT_LABELS } from "../audio/effects";

const clampSW = (w: number) => Math.max(70, Math.min(180, w || 0)) || 96;  // strip width

interface StripControls {
  fader: HTMLInputElement;
  pan?: HTMLInputElement;
  db: HTMLElement;
  mute?: HTMLButtonElement;
  solo?: HTMLButtonElement;
}

export class Mixer {
  el = el("div", { class: "mixer" });
  private stripsEl = el("div", { class: "mixer-strips" });
  private meters = new Map<string, { fill: HTMLElement; peak: HTMLElement; wrap: HTMLElement }>();
  private peakHold = new Map<string, number>();
  private clipUntil = new Map<string, number>();
  private controls = new Map<string, StripControls>();
  private sig = "";
  private outputs: { deviceId: string; label: string }[] = [];
  private sinkId = "";

  setOutputs(list: { deviceId: string; label: string }[]) { this.outputs = list; this.build(); }

  constructor(
    private store: Store,
    private engine: AudioEngine,
    private cb: { openFx: (target: string | "master", fxId: string) => void; addFx: (target: string | "master") => void },
  ) {
    const collapseBtn = el("button", { class: "mx-collapse", text: "▾", attrs: { title: "Minimize / show mixer" } });
    collapseBtn.addEventListener("click", () => {
      this.collapsed = !this.collapsed;
      this.el.classList.toggle("collapsed", this.collapsed);
      collapseBtn.textContent = this.collapsed ? "▴" : "▾";
      collapseBtn.title = this.collapsed ? "Show mixer" : "Minimize mixer";
      if (!this.collapsed) this.build(); // refresh after being hidden
    });
    // strip-width control (mixer "width") — sets a CSS var the strips read
    const wIn = el("input", { attrs: { type: "range", min: "70", max: "180", step: "2", title: "Channel strip width" } }) as HTMLInputElement;
    wIn.style.width = "80px";
    const stripW = clampSW(Number(localStorage.getItem("webcut.stripWidth")) || 96);
    wIn.value = String(stripW);
    this.el.style.setProperty("--strip-w", stripW + "px");
    wIn.addEventListener("input", () => {
      const v = clampSW(Number(wIn.value));
      this.el.style.setProperty("--strip-w", v + "px");
      localStorage.setItem("webcut.stripWidth", String(v));
    });

    const bar = el("div", { class: "mx-bar" }, [el("b", { text: "Mixer" }), el("div", { class: "spacer" }), el("span", { class: "hint", text: "Width" }), wIn, collapseBtn]);
    // The mixer is the bottom-right panel; its strips fill the panel height (CSS),
    // and its panel width is governed by the bottom-row divider.
    this.el.append(bar, this.stripsEl);
    this.build();
    store.on("change", ({ scope }) => {
      if (scope === "transport" || scope === "selection" || this.collapsed) return;
      // Only rebuild the DOM on structural changes; otherwise update values in
      // place so dragging a fader/pan never destroys the element being dragged.
      if (this.structureSig() !== this.sig) this.build();
      else this.refreshStates();
    });
  }
  private collapsed = false;

  /** Only audio/instrument tracks have a mixer channel — video lanes are visual. */
  private mixTracks() { return this.store.project.tracks.filter((t) => t.kind !== "video"); }

  private structureSig(): string {
    const p = this.store.project;
    const fxSig = (fx: { id: string; enabled: boolean }[]) => fx.map((f) => f.id + (f.enabled ? "1" : "0")).join(",");
    return this.mixTracks().map((t) => `${t.id}~${t.name}~${t.color}~${fxSig(t.fx)}`).join("|") + "#M~" + fxSig(p.masterFx);
  }

  /** Update mute/solo/value states without rebuilding (keeps dragged sliders alive). */
  private refreshStates() {
    for (const t of this.mixTracks()) {
      const c = this.controls.get(t.id);
      if (!c) continue;
      c.mute?.classList.toggle("on", t.mute);
      c.solo?.classList.toggle("on", t.solo);
      if (document.activeElement !== c.fader) c.fader.value = String(t.volume);
      if (c.pan && document.activeElement !== c.pan) c.pan.value = String(t.pan);
      c.db.textContent = fmtDb(t.volume);
    }
    const m = this.controls.get("master");
    if (m) {
      if (document.activeElement !== m.fader) m.fader.value = String(this.store.project.masterVolume);
      m.db.textContent = fmtDb(this.store.project.masterVolume);
    }
  }

  private strip(opts: {
    id: string; title: string; master?: boolean;
    volume: number; pan?: number; mute?: boolean; solo?: boolean;
    fx: { id: string; name: string; enabled: boolean }[];
    color?: string;
  }): HTMLElement {
    const s = el("div", { class: "strip" + (opts.master ? " master" : "") });
    const title = el("div", { class: "title", text: opts.title });
    if (opts.color) title.style.color = opts.color;

    const fxBox = el("div", { class: "fx" });
    for (const fx of opts.fx) {
      const b = el("button", {}, [
        el("span", { text: fx.name }),
        el("span", { text: fx.enabled ? "●" : "○" }),
      ]);
      b.title = "Edit / toggle effect";
      b.addEventListener("click", (e) => {
        if ((e as MouseEvent).shiftKey) { // shift-click toggles bypass
          this.store.updateEffect(opts.id, fx.id, { enabled: !fx.enabled });
        } else this.cb.openFx(opts.id, fx.id);
      });
      fxBox.append(b);
    }
    const addFx = el("button", { class: "addfx", text: "+ Insert FX" });
    addFx.addEventListener("click", () => this.cb.addFx(opts.id));
    fxBox.append(addFx);

    // pan
    let panRow: HTMLElement | null = null;
    let panInput: HTMLInputElement | undefined;
    if (!opts.master && opts.pan !== undefined) {
      const pan = el("input", { class: "pan", attrs: { type: "range", min: "-1", max: "1", step: "0.01", value: String(opts.pan) } }) as HTMLInputElement;
      pan.addEventListener("input", () => this.store.updateTrack(opts.id, { pan: Number(pan.value) }));
      panInput = pan;
      panRow = el("div", { class: "knob-row" }, [el("span", { text: "L" }), pan, el("span", { text: "R" })]);
    }

    // fader + meter
    const fader = el("input", { class: "vfader", attrs: { type: "range", min: "0", max: "1.5", step: "0.01", value: String(opts.volume) } }) as HTMLInputElement;
    fader.addEventListener("input", () => {
      const v = Number(fader.value);
      if (opts.master) { this.store.project.masterVolume = v; this.store.emitChange("mixer"); }
      else this.store.updateTrack(opts.id, { volume: v });
      db.textContent = fmtDb(v);
    });
    const fill = el("i");
    const peakMark = el("b", { class: "peak" });
    const meterWrap = el("div", { class: "stripmeter" }, [fill, peakMark]);
    this.meters.set(opts.id, { fill, peak: peakMark, wrap: meterWrap });
    const faderArea = el("div", { class: "fader-area" }, [fader, meterWrap]);

    const db = el("output", { text: fmtDb(opts.volume) });
    db.style.fontSize = "10px"; db.style.color = "var(--muted)";

    // mute/solo
    let ms: HTMLElement | null = null;
    let mBtn: HTMLButtonElement | undefined, soBtn: HTMLButtonElement | undefined;
    if (!opts.master) {
      const m = el("button", { class: "mini m" + (opts.mute ? " on" : ""), text: "M" });
      m.addEventListener("click", () => this.store.updateTrack(opts.id, { mute: !this.store.track(opts.id)?.mute }, "tracks"));
      const so = el("button", { class: "mini s" + (opts.solo ? " on" : ""), text: "S" });
      so.addEventListener("click", () => this.store.updateTrack(opts.id, { solo: !this.store.track(opts.id)?.solo }, "tracks"));
      mBtn = m; soBtn = so;
      ms = el("div", { class: "mute-solo" }, [m, so]);
    }

    this.controls.set(opts.id, { fader, pan: panInput, db, mute: mBtn, solo: soBtn });

    s.append(title, fxBox);
    if (panRow) s.append(panRow);
    s.append(faderArea, db);
    if (ms) s.append(ms);

    // Master: audio output device selector (where supported).
    if (opts.master && this.outputs.length && typeof (this.engine.ctx as any).setSinkId === "function") {
      const outSel = el("select", { attrs: { title: "Audio output device" } }) as HTMLSelectElement;
      outSel.style.width = "100%"; outSel.style.fontSize = "10px"; outSel.style.marginTop = "6px";
      for (const d of this.outputs) {
        const o = el("option", { text: d.label, attrs: { value: d.deviceId } }) as HTMLOptionElement;
        if (this.sinkId === d.deviceId) o.selected = true;
        outSel.append(o);
      }
      outSel.addEventListener("change", async () => { this.sinkId = outSel.value; await this.engine.setSinkId(outSel.value); });
      s.append(el("div", { class: "hint", text: "Output" }), outSel);
    }
    return s;
  }

  build() {
    this.stripsEl.innerHTML = "";
    this.meters.clear();
    this.controls.clear();
    this.sig = this.structureSig();
    for (const t of this.mixTracks()) {
      this.stripsEl.append(this.strip({
        id: t.id, title: t.name, color: t.color,
        volume: t.volume, pan: t.pan, mute: t.mute, solo: t.solo,
        fx: t.fx.map((f) => ({ id: f.id, name: f.kind === "wam" ? f.name : EFFECT_LABELS[f.kind], enabled: f.enabled })),
      }));
    }
    this.stripsEl.append(this.strip({
      id: "master", title: "MASTER", master: true,
      volume: this.store.project.masterVolume,
      fx: this.store.project.masterFx.map((f) => ({ id: f.id, name: f.kind === "wam" ? f.name : EFFECT_LABELS[f.kind], enabled: f.enabled })),
    }));
  }

  updateMeters(levels: { master: number; tracks: Record<string, number> }) {
    const now = performance.now();
    for (const [id, m] of this.meters) {
      const lvl = id === "master" ? levels.master : (levels.tracks[id] ?? 0);
      m.fill.style.height = Math.min(100, lvl * 100) + "%";
      // peak-hold marker that slowly falls
      const held = Math.max(lvl, (this.peakHold.get(id) ?? 0) * 0.985);
      this.peakHold.set(id, held);
      m.peak.style.bottom = Math.min(100, held * 100) + "%";
      // clip indicator latches red for ~1.2 s when it hits/exceeds 0 dBFS
      if (lvl >= 0.99) this.clipUntil.set(id, now + 1200);
      m.wrap.classList.toggle("clip", now < (this.clipUntil.get(id) ?? 0));
    }
  }
}

function fmtDb(linear: number): string {
  const db = gainToDb(linear);
  return db === -Infinity ? "-∞" : `${db >= 0 ? "+" : ""}${db.toFixed(1)} dB`;
}
